import {
  EventStatus,
  StatusListWithApproverStates,
  statusListWithApproverStates,
  ChipsFilterOption
} from '@hconnect/common/types'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {EventsStatusesCount} from '../types'

const uniqueSystemChangeRequestStatuses: EventStatus<StatusListWithApproverStates>[] = Array.from(
  new Set(statusListWithApproverStates.map((status) => status))
)

export type UseSystemChangeRequestFiltersReturn = {
  value: EventStatus<StatusListWithApproverStates>[]
  onChange: (newValue: EventStatus<StatusListWithApproverStates>[]) => void
  options: ChipsFilterOption<EventStatus<StatusListWithApproverStates>>[]
  customAllLabel?: string
}

export const useSystemChangeRequestsFilters = (
  statusesCountMap?: EventsStatusesCount
): UseSystemChangeRequestFiltersReturn => {
  const [value, setValue] = useState<EventStatus<StatusListWithApproverStates>[]>([])
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const options: ChipsFilterOption<EventStatus<StatusListWithApproverStates>>[] = useMemo(() => {
    return uniqueSystemChangeRequestStatuses.map((status) => ({
      value: status,
      label: getStatusLabelWithCount({
        label: t(`shiftEvent.status.${status}`),
        status,
        statusesCountMap
      })
    }))
  }, [statusesCountMap, t])

  const handleChange = (newValue: EventStatus<StatusListWithApproverStates>[]) => {
    setValue(newValue)
  }

  const allStatusesCount = getAllStatusesCount(statusesCountMap)
  const customAllLabel = withStatusCountSuffix(t('chipsFilter.all'), allStatusesCount)

  return {
    value,
    onChange: handleChange,
    options,
    customAllLabel
  }
}

function getAllStatusesCount(statusesCountMap?: EventsStatusesCount) {
  return Object.values(statusesCountMap || {}).reduce((acc, count) => acc + count, 0)
}

function getStatusLabelWithCount({
  label,
  status,
  statusesCountMap
}: {
  label: string
  status: EventStatus<StatusListWithApproverStates>
  statusesCountMap?: EventsStatusesCount
}): string {
  const count = statusesCountMap ? statusesCountMap[status] : undefined
  return withStatusCountSuffix(label, count)
}

function withStatusCountSuffix(label: string, count: number | undefined): string {
  return count ? `${label} (${count})` : label
}
